import { ReactNode } from 'react';
import { forwardRef } from '@heroui/system';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardProps,
  CustomAvatarProps,
  DropdownItemType,
  IconButton,
  IconButtonProps,
  TmdxDropdownMenu,
} from '@ds';
import { CustomUserProps, TmdxUser } from '@ui';
import { TimeEditIcon } from '@icons';
import { BsThreeDots } from 'react-icons/bs';
import { MdPushPin } from 'react-icons/md';
import { UserInfoModal } from '@features/modals/UserInfoModal';
import { EmployeeComposed } from '@/api/apis/resources';

export enum CaseNoteMenuAction {
  Pin = 'pin',
  Edit = 'edit',
  Delete = 'delete',
}

interface CaseNotesProps {
  title: CustomUserProps['title'];
  children?: ReactNode;
  date?: string;
  variant: 'lg' | 'md';
  isWindowView?: boolean;
  isReadOnly?: boolean;
  isEdited?: boolean;
  isPinned?: boolean;
  isPinnedLight?: boolean;
  isScrollable?: boolean;
  withEditMenuItem?: boolean;
  withDeleteMenuItem?: boolean;
  onMenuAction?: (action: CaseNoteMenuAction) => void;
  caseNoteUserData?: EmployeeComposed;
}

interface CombinedCaseNotesProps extends CaseNotesProps {
  avatarProps?: CustomAvatarProps;
  buttonProps?: IconButtonProps;
  cardProps?: CardProps;
}

export const CaseNote = forwardRef<'div', CombinedCaseNotesProps>(
  (
    {
      title,
      date,
      variant = 'lg',
      isReadOnly,
      isWindowView,
      isEdited,
      isPinned,
      isPinnedLight,
      isScrollable = false,
      withEditMenuItem,
      withDeleteMenuItem,
      avatarProps,
      buttonProps,
      cardProps,
      onMenuAction,
      children,
      caseNoteUserData,
    },
    ref,
  ) => {
    const dropdownItems: DropdownItemType[] = [
      {
        key: CaseNoteMenuAction.Pin,
        label: isPinned ? 'Unpin' : 'Pin',
        color: 'primary',
      },
      {
        key: CaseNoteMenuAction.Edit,
        label: 'Edit',
        color: 'primary',
        className: `${!withEditMenuItem && 'hidden'}`,
      },
      {
        key: CaseNoteMenuAction.Delete,
        label: 'Delete',
        color: 'danger',
        className: `${!withDeleteMenuItem && 'hidden'}`,
      },
    ];

    return (
      <Card
        ref={ref}
        classNames={{
          base: `tmdx-case-note p-3 rounded-xl ${(!isWindowView && !isPinned) || isPinnedLight ? 'bg-base-80' : 'bg-base-70'} ${isPinned && !isPinnedLight && 'bg-base-60'} shadow-none overflow-visible w-full`,
          body: `tmdx-case-note__body mt-3 ${variant === 'lg' ? 'text-m-r!' : 'text-s-r!'} text-black p-0 ${isScrollable ? 'max-h-60' : ''}`,
          header: 'tmdx-case-note__head flex justify-between gap-2 p-0 relative',
          footer: 'tmdx-case-note__date flex items-center justify-end p-0 text-info-40 mt-1 overflow-visible',
        }}
        {...cardProps}
      >
        <CardHeader>
          <UserInfoModal user={caseNoteUserData}>
            <TmdxUser
              title={title}
              titleSize="md"
              titleColor="lightBlack"
              isPin
              showIcon={isPinned || isPinnedLight}
              icon={<MdPushPin size={16} />}
              avatarProps={{
                size: 'md',
                radius: 'md',
                ...avatarProps,
              }}
            />
          </UserInfoModal>
          {!isReadOnly && (
            <TmdxDropdownMenu
              items={dropdownItems}
              triggerContent={
                <IconButton variant="light" size="xs" startContent={<BsThreeDots size={16} />} {...buttonProps} />
              }
              placement="bottom-end"
              onAction={(key) => onMenuAction?.(key as CaseNoteMenuAction)}
              dropdownProps={{
                classNames: {
                  content: 'min-w-34 p-0 shadow-none',
                },
              }}
              menuProps={{
                'aria-label': 'Case note actions',
                classNames: {
                  base: 'max-w-34',
                },
              }}
            />
          )}
        </CardHeader>
        {children && <CardBody>{children}</CardBody>}

        {date && (
          <CardFooter>
            {isEdited && <TimeEditIcon className="mr-1 size-3.5" />}
            <span className={`text-base-20 ${variant === 'lg' ? 'text-s-r' : 'text-xs-r!'} leading-none`}>{date}</span>
          </CardFooter>
        )}
      </Card>
    );
  },
);
